import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Eye, Monitor, MoreHorizontal, Share2, Users, X } from 'lucide-react';
import type { WorkspaceFile } from '@auw/workspace-utils';
import { Tooltip } from '@auw/workspace-ui';
import { getWorkspaceFileIcon } from './fileContext';
import i18n from '@auw/i18n';

interface SharedMemberSummary {
  user_id: string;
  name?: string | null;
  email?: string | null;
  picture?: string | null;
}

interface WorkspaceHeaderFileActionsProps {
  sessionFiles: WorkspaceFile[];
  previewFileId: string | null;
  open: boolean;
  buttonRef: React.Ref<HTMLButtonElement>;
  panelRef: React.Ref<HTMLDivElement>;
  onToggle: () => void;
  onFileClick: (file: WorkspaceFile) => void;
  onFileRemove: (event: React.MouseEvent, fileId: string) => void;
}

interface WorkspaceHeaderCollabActionsProps {
  title: string;
  conversationId: string | null;
  members: SharedMemberSummary[];
  currentUserId?: string | null;
  currentUserPicture?: string | null;
  avatarErrorIds: Record<string, boolean>;
  onAvatarError: (avatarKey: string) => void;
  hasShareableMessages: boolean;
  onOpenShare: () => void;
  shouldShowGroupAction: boolean;
  isSharedGroupConversation: boolean;
  onOpenGroup: () => void;
}

interface WorkspaceHeaderOverflowButtonProps {
  buttonRef: React.Ref<HTMLButtonElement>;
  onOpen: () => void;
}

interface WorkspaceHeaderLiveViewActionProps {
  available: boolean;
  open: boolean;
  running: boolean;
  onToggle: () => void;
}

export const WorkspaceHeaderFileActions: React.FC<WorkspaceHeaderFileActionsProps> = ({
  sessionFiles,
  previewFileId,
  open,
  buttonRef,
  panelRef,
  onToggle,
  onFileClick,
  onFileRemove,
}) => {
  if (sessionFiles.length === 0) return null;

  return (
    <ActionGroup>
      <Tooltip label={i18n.t('common:tooltip.contextFile')} position="bottom">
        <IconButton
          ref={buttonRef}
          type="button"
          $active={open}
          onClick={onToggle}
        >
          <Eye size={16} />
          <BadgeCount>{sessionFiles.length}</BadgeCount>
        </IconButton>
      </Tooltip>
      {open && (
        <FilePanel ref={panelRef}>
          <FilePanelTitle>{i18n.t('common:tooltip.contextFile')}</FilePanelTitle>
          <FilePanelList>
            {sessionFiles.map((file) => {
              const isActive = previewFileId === file.file_id;
              const isUpload = file.source_type?.includes('upload');
              return (
                <FileItem
                  key={file.file_id}
                  $active={isActive}
                  role="button"
                  tabIndex={0}
                  onClick={() => onFileClick(file)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onFileClick(file);
                    }
                  }}
                >
                  <FileIcon>{getWorkspaceFileIcon(file.mime_type, file.original_name)}</FileIcon>
                  <FileName title={file.original_name}>{file.original_name}</FileName>
                  <SourceChip $type={isUpload ? 'upload' : 'generated'}>
                    {isUpload ? i18n.t('common:chat.upload') : i18n.t('common:button.create')}
                  </SourceChip>
                  <Tooltip label={i18n.t('common:tooltip.removeFromContext')} position="top">
                    <RemoveButton type="button" onClick={(event) => onFileRemove(event, file.file_id)}>
                      <X size={12} />
                    </RemoveButton>
                  </Tooltip>
                </FileItem>
              );
            })}
          </FilePanelList>
        </FilePanel>
      )}
    </ActionGroup>
  );
};

export const WorkspaceHeaderCollabActions: React.FC<WorkspaceHeaderCollabActionsProps> = ({
  title,
  conversationId,
  members,
  currentUserId,
  currentUserPicture,
  avatarErrorIds,
  onAvatarError,
  hasShareableMessages,
  onOpenShare,
  shouldShowGroupAction,
  isSharedGroupConversation,
  onOpenGroup,
}) => {
  const visibleMembers = members.slice(0, 3);
  const extraMemberCount = Math.max(0, members.length - visibleMembers.length);

  return (
    <>
      {members.length > 0 && (
        <AvatarStackWrap title={title}>
          {visibleMembers.map((member, index) => {
            const memberPicture = member.user_id === currentUserId
              ? (currentUserPicture ?? null)
              : (member.picture ?? null);
            const avatarKey = `active:${conversationId || 'new'}:${member.user_id}:${memberPicture || 'none'}`;
            const fallback = (member.name || member.email || member.user_id).charAt(0).toUpperCase();
            return (
              <AvatarCircle
                key={`${member.user_id}-${index}`}
                $idx={index}
                title={member.name || member.email || member.user_id}
              >
                {memberPicture && !avatarErrorIds[avatarKey] ? (
                  <AvatarImage
                    src={memberPicture}
                    alt={member.name || member.user_id}
                    onError={() => onAvatarError(avatarKey)}
                  />
                ) : (
                  <span>{fallback}</span>
                )}
              </AvatarCircle>
            );
          })}
          {extraMemberCount > 0 && (
            <ExtraCount $idx={visibleMembers.length}>+{extraMemberCount}</ExtraCount>
          )}
        </AvatarStackWrap>
      )}
      {hasShareableMessages && (
        <Tooltip label={i18n.t('common:tooltip.share')} position="bottom">
          <IconButton type="button" onClick={onOpenShare}>
            <Share2 size={16} />
          </IconButton>
        </Tooltip>
      )}
      {shouldShowGroupAction && (
        <Tooltip
          label={isSharedGroupConversation ? i18n.t('workspace:header.inviteTeamMember') : i18n.t('workspace:header.startGroupChat')}
          position="bottom"
          align="end"
        >
          <IconButton type="button" onClick={onOpenGroup}>
            <Users size={16} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export const WorkspaceHeaderOverflowButton: React.FC<WorkspaceHeaderOverflowButtonProps> = ({
  buttonRef,
  onOpen,
}) => {
  return (
    <Tooltip label={i18n.t('common:tooltip.moreActions')} position="bottom">
      <IconButton
        ref={buttonRef}
        type="button"
        onClick={onOpen}
      >
        <MoreHorizontal size={16} />
      </IconButton>
    </Tooltip>
  );
};

export const WorkspaceHeaderLiveViewAction: React.FC<WorkspaceHeaderLiveViewActionProps> = ({
  available,
  open,
  running,
  onToggle,
}) => {
  if (!available) return null;
  return (
    <Tooltip label={open ? i18n.t('common:tooltip.closeLiveBrowser') : i18n.t('common:tooltip.openLiveBrowser')} position="bottom">
      <LiveViewButton type="button" $active={open} $running={running} onClick={onToggle}>
        <Monitor size={16} />
      </LiveViewButton>
    </Tooltip>
  );
};

const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.42; }
  100% { opacity: 1; }
`;

const ActionGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const IconButton = styled.button<{ $active?: boolean }>`
  position: relative;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme, $active }) =>
    $active
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.72)')
      : (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.56)')};
  border-radius: 999px;
  background: ${({ theme, $active }) =>
    $active
      ? (theme.mode === 'dark' ? 'rgba(36, 38, 44, 0.62)' : 'rgba(255, 255, 255, 0.58)')
      : (theme.mode === 'dark' ? 'rgba(18, 20, 24, 0.42)' : 'rgba(255, 255, 255, 0.38)')};
  color: ${({ theme }) => theme.mode === 'dark' ? '#fff' : '#000'};
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 10px 24px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05)'
    : '0 10px 24px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.48)'};
  backdrop-filter: blur(18px) saturate(170%);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(48, 51, 58, 0.68)' : 'rgba(255, 255, 255, 0.52)'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.74)'};
    color: ${({ theme }) => theme.mode === 'dark' ? '#fff' : '#000'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const LiveViewButton = styled(IconButton)<{ $running: boolean }>`
  color: ${({ theme, $active }) => ($active ? theme.colors.accent : (theme.mode === 'dark' ? '#fff' : '#000'))};

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }

  ${({ $running }) => $running && css`
    animation: ${blink} 1.4s infinite ease-in-out;
  `}
`;

const BadgeCount = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 14px;
  height: 14px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.bgPrimary};
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 14px;
`;

const FilePanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: min(360px, calc(100vw - 32px));
  max-height: 320px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSecondary};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 20;
`;

const FilePanelTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const FilePanelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
`;

const FileItem = styled.div<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme, $active }) => ($active ? theme.colors.bgHover : theme.colors.bgPrimary)};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const FileIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FileName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SourceChip = styled.span<{ $type: 'upload' | 'generated' }>`
  padding: 3px 6px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme, $type }) => ($type === 'upload' ? theme.colors.textSecondary : theme.colors.accent)};
  background: ${({ theme, $type }) => (
    $type === 'upload'
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'
      : theme.mode === 'dark' ? 'rgba(88,166,255,0.18)' : 'rgba(0,122,255,0.12)'
  )};
`;

const RemoveButton = styled.button`
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const AvatarStackWrap = styled.div`
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
`;

const AvatarCircle = styled.div<{ $idx: number }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: ${({ $idx }) => ($idx === 0 ? 0 : -8)}px;
  border: 1px solid ${({ theme }) => theme.colors.bgPrimary};
  background: ${({ theme }) => theme.colors.bgHover};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.75rem;
  font-weight: 600;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ExtraCount = styled.span<{ $idx: number }>`
  min-width: 26px;
  height: 26px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: ${({ $idx }) => ($idx === 0 ? 0 : -8)}px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.bgPrimary};
  background: ${({ theme }) => theme.colors.bgHover};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.6875rem;
  font-weight: 700;
`;
